import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import MainLayout from '../components/layout/MainLayout';
import Section from '../components/ui/Section';
import supabase from '../supabase/supabase';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { mockResources } from '../data/mockData';

const ResourceDetail = () => {
  const { slug } = useParams();
  const [article, setArticle] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchArticle = async () => {
      setLoading(true);
      try {
        const { data, error } = await supabase
          .from('resources_1779569461628')
          .select('*')
          .eq('slug', slug)
          .single();

        if (data) {
          setArticle(data);
        } else {
          // Fallback to mock data if not found in DB
          const mockMatch = mockResources.find(r => r.slug === slug);
          setArticle(mockMatch || null);
        }
      } catch (err) {
        console.error('Error fetching article:', err);
        const mockMatch = mockResources.find(r => r.slug === slug);
        setArticle(mockMatch || null);
      } finally {
        setLoading(false);
      }
    };

    fetchArticle();
  }, [slug]);

  if (loading) {
    return (
      <MainLayout>
        <div className="min-h-[60vh] flex items-center justify-center">
          <SafeIcon icon={FiIcons.FiLoader} className="w-10 h-10 text-brand-teal animate-spin" />
        </div>
      </MainLayout>
    );
  }

  if (!article) {
    return (
      <MainLayout>
        <Section bg="cream" className="min-h-[60vh] flex items-center">
          <div className="max-w-2xl mx-auto text-center">
            <h1 className="text-3xl font-bold text-brand-navy mb-4">Article Not Found</h1>
            <p className="text-gray-600 mb-8">The resource you're looking for might have been moved or deleted.</p>
            <Link to="/resources" className="text-brand-teal font-bold hover:underline flex items-center justify-center gap-2">
              <SafeIcon icon={FiIcons.FiArrowLeft} /> Back to Resources
            </Link>
          </div>
        </Section>
      </MainLayout>
    );
  }

  return (
    <MainLayout>
      <article className="bg-white">
        <Section bg="cream" className="pt-24 pb-16">
          <div className="max-w-3xl mx-auto">
            <Link to="/resources" className="text-brand-teal font-bold text-sm uppercase tracking-widest mb-8 inline-flex items-center gap-2 hover:gap-3 transition-all">
              <SafeIcon icon={FiIcons.FiArrowLeft} /> Back to Resources
            </Link>
            <div className="mb-6">
              <span className="bg-brand-teal/10 text-brand-teal px-3 py-1 rounded-full text-xs font-bold uppercase tracking-wider">
                {article.category}
              </span>
            </div>
            <h1 className="text-4xl md:text-5xl font-bold text-brand-navy leading-tight mb-6">
              {article.title}
            </h1>
            <div className="flex items-center gap-4 text-gray-500 text-sm">
              <div className="flex items-center gap-2">
                <div className="w-8 h-8 rounded-full bg-brand-navy/10 flex items-center justify-center font-bold text-brand-navy">TB</div>
                <span>Tomar Beh</span>
              </div>
              <span>•</span>
              <span>{new Date(article.created_at).toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' })}</span>
            </div>
          </div>
        </Section>

        <Section bg="white" className="py-16">
          <div className="max-w-3xl mx-auto px-4">
            <div className="prose prose-lg prose-teal max-w-none">
              <ReactMarkdown 
                remarkPlugins={[remarkGfm]}
                components={{
                  h2: ({node, ...props}) => <h2 className="text-3xl font-bold text-brand-navy mt-12 mb-6" {...props} />,
                  h3: ({node, ...props}) => <h3 className="text-2xl font-bold text-brand-navy mt-8 mb-4" {...props} />,
                  p: ({node, ...props}) => <p className="text-gray-700 leading-relaxed mb-6 text-lg" {...props} />,
                  ul: ({node, ...props}) => <ul className="space-y-3 mb-8 list-none" {...props} />,
                  li: ({node, ...props}) => (
                    <li className="flex items-start gap-3 text-gray-700 text-lg">
                      <span className="text-brand-teal font-bold mt-1.5 shrink-0">•</span>
                      <span>{props.children}</span>
                    </li>
                  ),
                  blockquote: ({node, ...props}) => (
                    <blockquote className="border-l-4 border-brand-teal bg-brand-teal/5 p-8 my-10 rounded-r-2xl italic text-brand-navy text-xl" {...props} />
                  )
                }}
              >
                {article.content}
              </ReactMarkdown>
            </div>

            <div className="mt-20 pt-10 border-t border-gray-100">
              <div className="bg-brand-cream p-8 rounded-3xl flex flex-col md:flex-row items-center justify-between gap-8">
                <div>
                  <h3 className="text-2xl font-bold text-brand-navy mb-2">Enjoyed this article?</h3>
                  <p className="text-gray-600">Join my newsletter for more practical AI leadership insights.</p>
                </div>
                <Link to="/newsletter" className="bg-brand-teal text-white px-8 py-4 rounded-xl font-bold hover:bg-teal-600 transition-colors whitespace-nowrap">
                  Join the Newsletter
                </Link>
              </div>
            </div>
          </div>
        </Section>
      </article>
    </MainLayout>
  );
};

export default ResourceDetail;